import AWS from 'aws-sdk';
import firebase from 'firebase/app';

import { timestamp } from '../datetime';
import { getDump } from '../system';

import { LOG_LEVEL } from './constants';
import promisify from './promisify';

const LOG_GROUP_NAME = 'FairViewWebsite';
const AWS_REGION = 'eu-west-2';
const AWS_IDENTITY_TOKEN = 'eu-west-2:48bcab89-0505-472d-a947-fbc6af626054';
const GENERAL_STREAM_NAME = 'general';

export class CloudWatchLogger {
  constructor() {
    this.reset();

    AWS.config.region = AWS_REGION;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: AWS_IDENTITY_TOKEN,
    });

    this.cloudWatchLogs = new AWS.CloudWatchLogs();
  }

  get streamName() {
    return firebase.auth().currentUser?.uid;
  }

  get streamParams() {
    return {
      logGroupName: LOG_GROUP_NAME,
      logStreamName: this.streamName,
    };
  }

  async getFingerprint() {
    if (!this.fingerprint) {
      this.fingerprint = await getDump();
    }

    return this.fingerprint;
  }

  async getStream() {
    if (!this.streamName) {
      return null;
    }

    return (await this.checkLogStreamExists()) || this.createLogStream();
  }

  async getGeneralStream() {
    if (this.generaStream) {
      return this.generaStream;
    }

    this.generaStream = await this.describeFistStreamByParams({
      logGroupName: LOG_GROUP_NAME,
      logStreamNamePrefix: GENERAL_STREAM_NAME,
    });

    return this.generaStream;
  }

  toLogEvents(message) {
    const logs = [
      {
        message: JSON.stringify(message),
        timestamp: timestamp(),
      },
    ];

    return logs;
  }

  toLogParams(message) {
    return {
      ...this.streamParams,
      logEvents: this.toLogEvents(message),
      sequenceToken: this.sequenceToken,
    };
  }

  async doAnonymousLog(log, logLevel) {
    const stream = await this.getGeneralStream();

    if (!stream) {
      return;
    }

    const message = {
      level: logLevel,
      log,
    };

    const params = this.toLogParams(message);

    if (!this.generalSequenceToken) {
      this.generalSequenceToken = stream.uploadSequenceToken;
    }

    this.generalSequenceToken = await this.sendLogs({
      ...params,
      logStreamName: GENERAL_STREAM_NAME,
      sequenceToken: this.generalSequenceToken,
    });
  }

  anonymousLog(log, logLevel = LOG_LEVEL.DEBUG) {
    try {
      this.doAnonymousLog(log, logLevel);
    } catch (e) {
      // just ignore
    }
  }

  async sendLogs(params) {
    const { nextSequenceToken } = await promisify(
      this.cloudWatchLogs.putLogEvents,
      this.cloudWatchLogs,
    )(params);

    return nextSequenceToken;
  }

  async doLog(log, logLevel) {
    this.checkForCacheReload();

    const stream = await this.getStream();

    if (!stream) {
      return;
    }

    if (!this.sequenceToken) {
      this.sequenceToken = stream.uploadSequenceToken;
    }

    const fingerprint = await this.getFingerprint();

    const message = {
      level: logLevel,
      log,
      fingerprint,
    };

    const params = this.toLogParams(message);
    this.sequenceToken = await this.sendLogs(params);
  }

  log(log, logLevel = LOG_LEVEL.DEBUG) {
    try {
      this.doLog(log, logLevel);
    } catch (e) {
      // just ignore
    }
  }

  createLogStream() {
    if (!this.streamName) {
      return null;
    }

    return promisify(this.cloudWatchLogs.createLogStream, this.cloudWatchLogs)(this.streamParams);
  }

  async checkLogStreamExists() {
    if (this.logStream) {
      return this.logStream;
    }

    const { logStreamName: logStreamNamePrefix, ...restParams } = this.streamParams;
    const params = {
      ...restParams,
      logStreamNamePrefix,
    };

    this.logStream = await this.describeFistStreamByParams(params);

    return this.logStream;
  }

  async describeFistStreamByParams(params) {
    const data = await promisify(
      this.cloudWatchLogs.describeLogStreams,
      this.cloudWatchLogs,
    )(params);

    return data.logStreams.length > 0 ? data.logStreams[0] : null;
  }

  checkForCacheReload() {
    const currentStreamName = this.streamName;
    if (this.prevStreamName !== currentStreamName) {
      this.reset();
      this.prevStreamName = currentStreamName;
    }
  }

  reset() {
    this.logStream = null;
    this.fingerprint = null;
    this.prevStreamName = null;
    this.sequenceToken = null;
  }
}
